import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UntypedFormBuilder } from '@angular/forms';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Observable } from 'rxjs/internal/Observable';

import { MatDialog } from '@angular/material/dialog';

import { TutorialPlayerDialogComponent } from '@app/modules/tutorials/tutorial-player-dialog/tutorial-player-dialog.component';
import { Tutorial } from '@app/models';

export interface TutorialApi {
  items: Tutorial[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class TutorialsService {
  private _apiRoute = '/api/tutorials/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  sourceTypeList: string[] = ['html', 'video', 'image'];

  private currentTutorialBeingViewed: Tutorial = null;

  categories = ['B2B', 'Recruiting', 'Administrative', 'RFPs', 'Credentialing', 'Job boards', 'Processes', 'Miscellaneous Helpful Tips and Tricks'];

  constructor(
    private _dialog: MatDialog,
    private httpClient: HttpClient,
    private formBuilder: UntypedFormBuilder
  ) { }


  setCurrentBeingViewed(_tutorial: Tutorial): void {
    this.currentTutorialBeingViewed = _tutorial;
  }

  getCurrentBeingViewed(): Tutorial {
    return this.currentTutorialBeingViewed;
  }


  get(_id: string): Observable<Tutorial> {
    return this.httpClient.get<Tutorial>(this._apiRoute + _id);
  }


  create(_seasonStandings: Tutorial): Observable<Tutorial> {
    return this.httpClient.post<Tutorial>(this._apiRoute, JSON.stringify(_seasonStandings), this.headerOptions);
  }


  search(searchTerms: Tutorial): Observable<Tutorial[]> {
    return this.httpClient.post<Tutorial[]>(this._apiRoute + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  selectAllSearch(searchTerms: Tutorial): Observable<Tutorial[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<Tutorial[]>(this._apiRoute + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  getDatasource(searchParams, sortField = 'createdAt', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<TutorialApi> {
    searchParams['selectAll'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<TutorialApi>(this._apiRoute + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  update(_seasonStandings: Tutorial): Observable<Tutorial> {
    return this.httpClient.put<any>(this._apiRoute + _seasonStandings._id, JSON.stringify(_seasonStandings), this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.delete(this._apiRoute + '/' + id, this.headerOptions);
  }


  tutorialFormGroup(formData: Tutorial) {
    return this.formBuilder.group({
      _id: [formData._id],
      name: [formData.name],
      description: [formData.description],
      category: [formData.category],
      htmlText: [formData.htmlText],
      sourceType: [formData.sourceType],
      sourcePath: [formData.sourcePath],
      active: [formData.active]
    });
  }


  public async loadVideo(_tutorialTitle: string) {
    // console.log('Loading video in popup dialog: ', _tutorialTitle);

    // console.log('Getting tutorial for parent and section.');

    const tutorialSearch = new Tutorial();
    tutorialSearch.active = true;
    tutorialSearch.name = _tutorialTitle;

    const tutorialsForSection = await lastValueFrom(this.search(tutorialSearch));
    // console.log('Tutorials for title: ', tutorialsForSection);

    let tutorialToShow = null;

    if (tutorialsForSection && tutorialsForSection.length) {
      tutorialToShow = tutorialsForSection[0];
      // console.log('Current Tutorial set to: ', tutorialToShow);

      if (tutorialToShow) {
        await this.showTutorialDialog(tutorialToShow);
        return;
      }
    }

    // console.log('Did not find a video to show.');
  }


  async showTutorialDialog(_tutorial: Tutorial): Promise<any> {
    return new Promise(async (resolve) => {
      const dialogRef = this._dialog.open(TutorialPlayerDialogComponent, { panelClass: 'white-bg' });
      const instance = dialogRef.componentInstance;
      instance.tutorialToPlay = _tutorial;

      await lastValueFrom(dialogRef.afterClosed());
      resolve(null);
    });
  }
}